<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useCampaignStore } from '@/stores';
import { closeModal, updateShow } from '@/app/components/modal/modals';
import type { ModalProps } from '@/app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { DATA_QA } from './delete-campaign.constants';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import { CampaignStateEnum } from '@/apis/generated/dialbunny';
import logger from '@/logger';
import { useRouter } from 'vue-router';

const log = logger.getChild('DeleteCampaign');
log.debug('Mounting');
const props = defineProps<ModalProps>();

if (!props.meta?.id) {
  log.error('Missing campaign ID in modal meta');
  void closeModal();
}

const mounted = ref(false);

onMounted(() => {
  log.debug('Mounted');
  mounted.value = true;
});

const router = useRouter();

const show = computed(() => mounted.value && !!watchCampaign.value.item);

const campaignStore = useCampaignStore();
const watchCampaign = campaignStore.watch(
  () => props.meta?.id ?? '',
  'DeleteCampaign',
);

const deleting = ref(false);

watch(
  () => watchCampaign.value.error,
  (error) => {
    if (error) {
      log.warn(
        `Error fetching campaign. Status: ${error.response.status}, Message: ${error.message}`,
      );
      void closeModal();
      // show toast
    }
  },
);

watch(
  [() => watchCampaign.value.item, () => watchCampaign.value.loading],
  ([campaignToDelete, loading]) => {
    if (!loading && !campaignToDelete) {
      log.log('Campaign was deleted');
      void closeModal();
      // show toast
    }
  },
);

watch(
  () => watchCampaign.value.item?.state,
  (state) => {
    if (
      state !== CampaignStateEnum.Scheduled &&
      state !== CampaignStateEnum.Pending
    ) {
      log.log('Campaign is already started or ended');
      void closeModal();
      // show toast
    }
  },
);

const confirm = async () => {
  if (!watchCampaign.value.item?.id) {
    log.error('Campaign ID is missing in watchCampaign');
    return;
  }
  log.debug('Deleting campaign, id:', watchCampaign.value.item.id);
  deleting.value = true;
  campaignStore.discard(watchCampaign.value.item.id, {
    onSuccess: async () => {
      // show toast
      deleting.value = false;
      await closeModal();
      await router.push({ name: 'campaigns' });
    },
    onRetry: () => {
      // show toast
    },
    onError: (error) => {
      log.log('Error deleting campaign', error);
      deleting.value = false;
      // show toast
    },
  });
};

const cancel = () => {
  void closeModal();
};
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('DELETE_CAMPAIGN_MODAL_TITLE')"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    {{ $t('DELETE_CAMPAIGN_DESCRIPTION') }}
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton :data-qa="DATA_QA.CANCEL" importance="clear" @click="cancel">
        {{ $t('DELETE_CAMPAIGN_CANCEL') }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.CONFIRM"
        kind="danger"
        importance="primary"
        class="d-ml6"
        @click="confirm"
      >
        {{ $t('DELETE_CAMPAIGN_CONFIRM') }}
      </DtButton>
    </div>
  </DtModal>
</template>
